import { Controller } from "@hotwired/stimulus"
import { EditorView, basicSetup } from "codemirror"
import { html as htmlLang } from "@codemirror/lang-html"
import { EditorState } from "@codemirror/state"

// Phase 2 : éditeur split desktop avec CodeMirror 6 + live preview debounce.
// Le textarea sous-jacent reste la source de vérité pour le submit du form.
// Live preview à droite (desktop), modal plein écran disponible partout.

const SNIPPETS = {
  hero: `<h1>Titre principal de la formation</h1>
<p>Sous-titre accrocheur décrivant l'objectif de la formation en une phrase.</p>
`,
  two_cols: `<div class="grid grid-cols-1 md:grid-cols-2 gap-6 my-6">
  <div>
    <h3>Objectifs</h3>
    <p>Décrire les compétences ciblées par cette formation.</p>
  </div>
  <div>
    <h3>Public visé</h3>
    <p>Préciser le profil idéal des participants.</p>
  </div>
</div>
`,
  cta: `<div class="bg-orange-50 border-l-4 border-orange-500 p-4 my-6">
  <p class="font-semibold">Prêt·e à vous lancer ?</p>
  <p><a href="https://arkoconsulting.org/contact" target="_blank" rel="noopener">S'inscrire à la prochaine session →</a></p>
</div>
`,
  features: `<h3>Ce que vous gagnerez</h3>
<ul>
  <li><strong>Modulaire</strong> — adapté à votre rythme</li>
  <li><strong>Pratique</strong> — études de cas concrets issus du terrain</li>
  <li><strong>Suivi</strong> — coaching individuel pendant 6 semaines</li>
</ul>
`,
  table: `<table>
  <thead>
    <tr><th>Profil</th><th>Tarif HT</th><th>Inclus</th></tr>
  </thead>
  <tbody>
    <tr><td>Salarié·e</td><td>1 800 €</td><td>Formation + supports + déjeuners</td></tr>
    <tr><td>Indépendant·e</td><td>1 200 €</td><td>Formation + supports + déjeuners</td></tr>
  </tbody>
</table>
`,
  quote: `<blockquote>
  « Cette formation m'a permis de structurer un dispositif d'évaluation que je portais depuis 2 ans sans cadre clair. »
  <br>— <em>Marie L., chargée de projet ONG</em>
</blockquote>
`
}

export default class extends Controller {
  static targets = [
    "fr", "en", "title", "status",
    "tabFr", "tabEn",
    "editorMount", "livePreview",
    "modal", "iframe"
  ]
  static values = { previewUrl: String }

  connect() {
    this.locale = "fr"
    this.debounceTimer = null
    this.editor = this.buildEditor(this.frTarget.value)
    this.scheduleLivePreview()
  }

  disconnect() {
    if (this.editor) this.editor.destroy()
    if (this.debounceTimer) clearTimeout(this.debounceTimer)
  }

  buildEditor(initialContent) {
    return new EditorView({
      doc: initialContent || "",
      extensions: [
        basicSetup,
        htmlLang(),
        EditorView.lineWrapping,
        EditorView.updateListener.of(update => {
          if (update.docChanged) this.handleDocChange()
        }),
        EditorView.theme({
          "&": { height: "100%", fontSize: "13px" },
          ".cm-scroller": { fontFamily: "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace" }
        })
      ],
      parent: this.editorMountTarget
    })
  }

  handleDocChange() {
    const content = this.editor.state.doc.toString()
    this.currentTextareaTarget().value = content
    this.scheduleLivePreview()
  }

  currentTextareaTarget() {
    return this.locale === "fr" ? this.frTarget : this.enTarget
  }

  switchToFr() { this.switchLocale("fr") }
  switchToEn() { this.switchLocale("en") }

  switchLocale(locale) {
    if (this.locale === locale) return
    // Persist current editor content into the active textarea
    this.currentTextareaTarget().value = this.editor.state.doc.toString()
    this.locale = locale
    // Reload editor with the other locale's content
    this.editor.dispatch({
      changes: { from: 0, to: this.editor.state.doc.length, insert: this.currentTextareaTarget().value || "" }
    })
    this.updateTabsStyle()
    this.scheduleLivePreview()
  }

  updateTabsStyle() {
    const active = "border-blue-600 text-blue-600"
    const inactive = "border-transparent text-gray-500 hover:text-gray-700"
    const all = "px-4 py-2 text-sm font-medium border-b-2"
    if (this.locale === "fr") {
      this.tabFrTarget.className = `${all} ${active}`
      this.tabEnTarget.className = `${all} ${inactive}`
    } else {
      this.tabFrTarget.className = `${all} ${inactive}`
      this.tabEnTarget.className = `${all} ${active}`
    }
  }

  insertSnippet(event) {
    const key = event.currentTarget.dataset.snippet
    const snippet = SNIPPETS[key]
    if (!snippet) return
    const { from } = this.editor.state.selection.main
    this.editor.dispatch({
      changes: { from, to: from, insert: snippet },
      selection: { anchor: from + snippet.length }
    })
    this.editor.focus()
  }

  scheduleLivePreview() {
    if (this.debounceTimer) clearTimeout(this.debounceTimer)
    if (!this.hasLivePreviewTarget) return
    this.setStatus("Rafraîchissement…")
    this.debounceTimer = setTimeout(() => this.refreshLivePreview(), 500)
  }

  async refreshLivePreview() {
    const content = this.editor.state.doc.toString()
    if (!content.trim()) {
      this.livePreviewTarget.srcdoc = `<html><body style="margin:0;padding:2rem;font-family:sans-serif;color:#9ca3af;font-style:italic">Aperçu vide — saisissez du HTML pour voir le rendu.</body></html>`
      this.setStatus("Aperçu vide")
      return
    }
    try {
      const html = await this.fetchPreview(content)
      this.livePreviewTarget.srcdoc = html
      this.setStatus("À jour ✓")
    } catch (e) {
      this.setStatus("Erreur de rafraîchissement")
      console.error("[formation-preview]", e)
    }
  }

  setStatus(text) {
    if (this.hasStatusTarget) this.statusTarget.textContent = text
  }

  // Plein écran via modal — même endpoint
  async openPreviewModal() {
    const content = this.editor.state.doc.toString()
    try {
      const html = await this.fetchPreview(content)
      this.iframeTarget.srcdoc = html
      this.modalTarget.classList.remove("hidden")
      this.modalTarget.classList.add("flex")
    } catch (e) {
      console.error("[formation-preview]", e)
      alert("Erreur lors du chargement de l'aperçu.")
    }
  }

  close() {
    this.modalTarget.classList.add("hidden")
    this.modalTarget.classList.remove("flex")
    this.iframeTarget.srcdoc = ""
  }

  backgroundClose(event) {
    if (event.target === this.modalTarget) this.close()
  }

  async fetchPreview(bodyHtml) {
    const csrf = document.querySelector('meta[name="csrf-token"]')?.content
    const res = await fetch(this.previewUrlValue, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-CSRF-Token": csrf || "",
        "Accept": "text/html"
      },
      body: JSON.stringify({
        body_html: bodyHtml,
        title: this.hasTitleTarget ? this.titleTarget.value : ""
      })
    })
    if (!res.ok) throw new Error(`HTTP ${res.status}`)
    return res.text()
  }
}
